import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Edit, PlaylistAddCheck } from '@mui/icons-material'
import { Box, IconButton, Switch, Tooltip, Typography } from '@mui/material'

import { GRTable, GRTableColumn } from '../../../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../../../components/GRTooltip/GRTooltip'
import { InternalCategoryFeature } from '../../../../types/InternalCategory'
import { TagAssignmentCount } from '../../../tags/components/TagAssignmentCount/TagAssignmentCount'
import { featureBulkAssignPath } from '../helpers/bulkAssignHelpers'
import { getChoiceNames, getNamesByIds } from '../helpers/categoryHelpers'
import { featureEditPath } from '../helpers/featureHelpers'

type Named = { id: string; name: string }

type CustomProps = {
  stores?: Named[]
  markets?: Named[]
  canToggle: boolean
  onToggleActive: (feature: InternalCategoryFeature) => void
}

type CategoryFeaturesTableProps = Omit<CustomProps, 'onToggleActive'> & {
  features: InternalCategoryFeature[]
  sortable: boolean
  onToggleActive: (feature: InternalCategoryFeature) => void
  onReorder: (featureIds: string[]) => void
}

const NameList: FC<{ title: string; names: string[] }> = ({ title, names }) => (
  <Box>
    <Typography variant="body2" fontWeight="bold" mb={0.5}>
      {title}
    </Typography>
    {names.length ? (
      names.map((name, index) => (
        <Typography key={`${name}-${index}`} variant="body2">
          {name}
        </Typography>
      ))
    ) : (
      <Typography variant="body2">–</Typography>
    )}
  </Box>
)

const PositionsCell: FC<{ feature: InternalCategoryFeature; stores?: Named[]; markets?: Named[] }> = ({ feature, stores, markets }) => {
  const { t } = useTranslation()
  return (
    <GRTooltip
      content={
        <Box display="flex" flexDirection="column" gap={1}>
          <NameList title={t('internal-analysts:categories_stores')} names={getNamesByIds(feature.stores, stores)} />
          <NameList title={t('internal-analysts:categories_markets')} names={getNamesByIds(feature.markets, markets)} />
        </Box>
      }
    >
      <Typography component="span" sx={{ textDecoration: 'underline dotted', cursor: 'default' }}>
        {feature.stores.length}, {feature.markets.length}
      </Typography>
    </GRTooltip>
  )
}

export const CategoryFeaturesTable: FC<CategoryFeaturesTableProps> = ({ features, stores, markets, canToggle, sortable, onToggleActive, onReorder }) => {
  const { t } = useTranslation()

  const tableColumns: GRTableColumn<InternalCategoryFeature, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-analysts:categories_feature_id'),
        accessor: ({ row }) => row.legacyId,
        headerCellProps: { sx: { width: 80 } },
      },
      {
        labelAccessor: t('internal-analysts:categories_feature_name'),
        accessor: ({ row }) =>
          row.name.trim() ? (
            <Typography fontWeight="bold">{row.name}</Typography>
          ) : (
            <Typography color="text.secondary">{t('internal-analysts:categories_unnamed_feature')}</Typography>
          ),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 280 } },
      },
      {
        labelAccessor: t('internal-analysts:categories_feature_active'),
        accessor: ({ row, customTableProps }) => (
          <Switch
            checked={!!row.active}
            disabled={!customTableProps?.canToggle}
            onChange={() => customTableProps?.onToggleActive(row)}
            inputProps={{ 'aria-label': t('internal-analysts:categories_feature_active_toggle', { name: row.name.trim() || row.legacyId }) }}
          />
        ),
        headerCellProps: { sx: { width: 90 } },
      },
      {
        labelAccessor: t('internal-analysts:categories_choices'),
        accessor: ({ row }) => <TagAssignmentCount names={getChoiceNames(row)} title={t('internal-analysts:categories_choices')} />,
        headerCellProps: { sx: { width: 90 } },
      },
      {
        labelAccessor: t('internal-analysts:categories_stores_markets'),
        accessor: ({ row, customTableProps }) => <PositionsCell feature={row} stores={customTableProps?.stores} markets={customTableProps?.markets} />,
        headerCellProps: { sx: { width: 130 } },
      },
      {
        labelAccessor: '',
        accessor: ({ row }) => (
          <Box display="flex" justifyContent="flex-end">
            <Tooltip title={t('internal-analysts:categories_edit_feature')}>
              <IconButton
                color="primary"
                aria-label={t('internal-analysts:categories_edit_feature_named', { name: row.name })}
                component={Link}
                to={featureEditPath(row.categoryId, row.id)}
              >
                <Edit />
              </IconButton>
            </Tooltip>
            <Tooltip title={row.markets.length ? t('internal-analysts:categories_bulk_assign') : t('internal-analysts:categories_bulk_assign_no_markets')}>
              <span>
                <IconButton
                  color="primary"
                  aria-label={t('internal-analysts:categories_bulk_assign_named', { name: row.name })}
                  component={Link}
                  to={featureBulkAssignPath(row.legacyId)}
                  disabled={!row.markets.length}
                >
                  <PlaylistAddCheck />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
        cellProps: { align: 'right' },
        headerCellProps: { sx: { width: 120, minWidth: 120 } },
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(() => ({ stores, markets, canToggle, onToggleActive }), [stores, markets, canToggle, onToggleActive])

  return (
    <GRTable
      rows={features}
      columns={columns}
      onColumnsUpdated={setColumns}
      rowIdKey={(row) => row.id}
      customProps={customProps}
      onSortRows={(rows) => onReorder(rows.map((row) => row.id))}
      sortable={sortable}
      disableSortTooltip={t('internal-analysts:categories_reorder_disabled')}
      noRowsLabel={t('internal-analysts:categories_no_features')}
      striped
      hoverable
    />
  )
}
